import { Injectable, signal } from '@angular/core'
import type { Notification } from '@awork/framework/models/notification.model'

export interface ToastOptions {
  /** Copies the message on activation and confirms in place. */
  copyOnTap?: boolean
  /** Display time in milliseconds, starting when this feedback replaces the previous entry. */
  hideDelay?: number
}

export type ToastContent =
  | { kind: 'feedback'; message: string; type: 'info' | 'warning' | 'confirm'; icon: string; options: ToastOptions }
  | { kind: 'notification'; notification: Notification; open: () => void }

export interface ToastEntry {
  content: ToastContent
  hideAt: number
}

@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly current = signal<ToastEntry[]>([])
  readonly entries = this.current.asReadonly()

  /**
   * Replaces feedback immediately without interrupting the notification.
   * @param {string} message Feedback text.
   * @param {'info' | 'warning' | 'confirm'} type Category controlling the icon and its colours.
   * @param {string} [icon=info] Optional information icon.
   * @param {ToastOptions} [options] Copy behavior and display time.
   */
  show(message: string, type: 'info' | 'warning' | 'confirm', icon = 'info', options: ToastOptions = {}): void {
    if (message) {
      this.display({ kind: 'feedback', message, type, icon, options }, options.hideDelay ?? 5000)
    }
  }

  /**
   * Replaces the notification immediately without interrupting feedback.
   * @param {Notification} notification Notification to display.
   * @param {() => void} [open] Action accepted when the toast is activated.
   */
  showNotification(notification: Notification, open: () => void = () => {}): void {
    this.display({ kind: 'notification', notification, open }, 5000)
  }

  /**
   * Removes the dismissed entry without removing a newer replacement.
   * @param {ToastEntry} entry Toast whose exit has completed.
   */
  dismiss(entry: ToastEntry): void {
    this.current.update(entries => entries.filter(current => current !== entry))
  }

  /**
   * Keeps one entry per type, with notifications above feedback.
   * @param {ToastContent} content Toast to display.
   * @param {number} delay Display time in milliseconds.
   */
  private display(content: ToastContent, delay: number): void {
    const entry = { content, hideAt: Date.now() + delay }

    this.current.update(entries => {
      const other = entries.filter(current => current.content.kind !== content.kind)

      return content.kind === 'notification' ? [entry, ...other] : [...other, entry]
    })
  }
}
